/**
 * TIER-1 wiring — client-side `.doc` export for approved documents.
 *
 * 1:1 behavioural port of the legacy `app.downloadArtifact` (project-docs.js
 * ~L540): it renders the artifact content into a Word-compatible HTML document
 * and downloads it as a `.doc` blob (Word opens HTML natively when the file
 * carries the `.doc` extension + `application/msword` mime).
 *
 * PK-QA-36 — the pure, DOM-free serializer (`exportLabel`, `renderDocContentHtml`
 * + its private `renderJd`/`renderInterviewQuestions`/… family, `buildDocHtml`,
 * `docFileName`, and the `SOURCING_SHEET_FIELDS` spec it depends on) was relocated
 * to `@eer/shared` (`docRender.ts`) so the api "final package" link-generation
 * endpoint renders each document through the SAME single grammar — no second,
 * divergable serializer. This file now owns ONLY the DOM/browser tail:
 *   - `downloadArtifactDoc` — compose the file name + trigger the browser
 *     download (`Blob` / `URL.createObjectURL` / `document.createElement` /
 *     anchor-click — the only DOM side-effect, guarded for SSR/tests).
 *
 * The imported renderers are pure and DOM-free (unit-tested in
 * `@eer/shared` `docRender.test.ts`). No `dangerouslySetInnerHTML` in React: this
 * HTML is only ever written into a Blob the browser downloads, never injected
 * into the live DOM.
 */
import { buildDocHtml, docFileName, exportLabel, renderDocContentHtml } from '@eer/shared';

/**
 * Download an approved artifact as a Word `.doc` file (legacy `downloadArtifact`).
 * Returns `false` when there is nothing to download (no content) so the caller
 * can surface the legacy "No content to download" toast.
 */
export function downloadArtifactDoc(params: {
  docType: string;
  content: unknown;
  clientName: string;
  roleName: string;
}): boolean {
  const { docType, content, clientName, roleName } = params;
  if (content == null || (typeof content === 'object' && Object.keys(content).length === 0)) {
    return false;
  }
  if (
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof Blob === 'undefined'
  ) {
    return false;
  }
  const label = exportLabel(docType);
  const client = clientName || 'project';
  const bodyHtml = renderDocContentHtml(docType, content);
  const docHtml = buildDocHtml({ label, clientName: client, roleName, bodyHtml });
  const blob = new Blob(['﻿' + docHtml], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = docFileName(docType, client, roleName);
  a.click();
  URL.revokeObjectURL(url);
  return true;
}
